/**
 * groupConversations — how the Xoe conversation rail decides what heading a row sits under.
 *
 * PURE, and no React, on purpose: the precedence rule below is the one piece of the rail
 * that is genuinely tricky, and it is much easier to defend in a table of inputs than
 * through a rendered component.
 *
 * ── THE PRECEDENCE, HIGHEST FIRST ────────────────────────────────────────────
 *
 * 1. SEARCHING → `flat`. One unheaded group, in server order. This is X6's rule verbatim:
 *    a search is the explicit act of looking PAST the record in front of you, so grouping
 *    the results re-imposes the structure the search just left behind. Grouping matches
 *    from every patient under a heading that says "This patient" would be labelling rows
 *    by a record nobody asked about.
 *
 * 2. A RECORD IS OPEN (and All-conversations is off) → `sectioned`. Exactly X7's three
 *    groups, with X7's predicates — including the de-duplication that stops "Currently
 *    open" rendering a row the other two also render. NO date buckets here.
 *
 * 3. OTHERWISE → `dated`. Today / Yesterday / Previous 7 days / Previous 30 days / month.
 *
 * *** WHY SECTIONING WINS OVER DATE GROUPING RATHER THAN NESTING INSIDE IT. *** Both
 * answer the same question — "why is this row here" — and two levels of that in a 240px
 * rail is noise. When a record is open, the record is the more urgent answer. And the
 * /xoe page's normal state (a practitioner sitting at `/xoe` for a long session) has NO
 * record open, which is exactly the state that gets date buckets.
 *
 * ⚠️ `last_message_at` IS NULLABLE ON A REAL, LISTED ROW. `send` creates the conversation
 * before the turn, and `ResearchDashboardPage` creates one before seeding, so a row can be
 * listed with no message yet. Bucketing falls back to `created_at`, which is what keeps
 * those out of an undated hole.
 */

import dayjs, { type Dayjs } from 'dayjs';

import type { XoeConversation } from '../../types/xoe/XoeTypes';
import type { RailGroup, BuildRailGroupsArgs, RailGroups } from '../../types/xoe/XoeUiTypes';

/** Date bucket slugs, widest-first is NOT the order — these are checked most-recent-first. */
const DATE_BUCKETS = [
	{ slug: 'today', heading: 'Today' },
	{ slug: 'yesterday', heading: 'Yesterday' },
	{ slug: 'previous-7-days', heading: 'Previous 7 days' },
	{ slug: 'previous-30-days', heading: 'Previous 30 days' },
] as const;

/**
 * Which bucket a conversation falls in. Returns a `{slug, heading}` for the four fixed
 * buckets, or a month bucket for anything older.
 */
const bucketFor = (conv: XoeConversation, now: Dayjs): { slug: string; heading: string } => {
	// `?? created_at` — see the header note. A brand-new conversation has no message yet.
	const stamp = dayjs(conv.last_message_at ?? conv.created_at);
	const startOfToday = now.startOf('day');

	if (!stamp.isValid()) {
		// An unparseable timestamp is a data problem, not a reason to drop the row from the
		// rail — the practitioner still needs to be able to reach the conversation.
		return { slug: 'undated', heading: 'Undated' };
	}
	if (!stamp.isBefore(startOfToday)) return DATE_BUCKETS[0];
	if (!stamp.isBefore(startOfToday.subtract(1, 'day'))) return DATE_BUCKETS[1];
	if (!stamp.isBefore(startOfToday.subtract(7, 'day'))) return DATE_BUCKETS[2];
	if (!stamp.isBefore(startOfToday.subtract(30, 'day'))) return DATE_BUCKETS[3];
	return { slug: stamp.format('YYYY-MM'), heading: stamp.format('MMMM YYYY') };
};

export function buildRailGroups({
	conversations,
	focusPatientId,
	activeUuid,
	showAll,
	searching,
	now = dayjs(),
}: BuildRailGroupsArgs): RailGroups {
	// ── 1. Searching flattens everything ──────────────────────────────────────
	if (searching) {
		return {
			mode: 'flat',
			groups: conversations.length
				? [{ key: 'search-results', heading: null, items: conversations }]
				: [],
			sectionedButEmpty: false,
		};
	}

	// ── 2. A record is open → X7's three sections ─────────────────────────────
	if (focusPatientId !== null && !showAll) {
		const inASection = (c: XoeConversation) =>
			c.patient_id === focusPatientId || c.patient_id === null;

		const patientItems = conversations.filter((c) => c.patient_id === focusPatientId);
		const generalItems = conversations.filter((c) => c.patient_id === null);
		// The open conversation when it belongs to some OTHER patient, and so appears in
		// neither section above. Filtered from the SAME array rather than tracked separately,
		// so it can never be a row the two sections also render — a duplicate carrying the
		// active highlight twice.
		const activeItems =
			activeUuid !== null
				? conversations.filter((c) => c.uuid === activeUuid && !inASection(c))
				: [];

		const groups: RailGroup[] = [];
		if (patientItems.length) {
			groups.push({
				key: 'patient',
				heading: 'This patient',
				testId: 'xoe-conversation-section-patient',
				items: patientItems,
			});
		}
		if (generalItems.length) {
			groups.push({
				key: 'general',
				heading: 'General',
				testId: 'xoe-conversation-section-general',
				items: generalItems,
			});
		}
		// LAST, not first: this is an exception row, and hoisting it would push the record's
		// own threads down the rail every time the practitioner happens to be mid-conversation
		// with somebody else.
		if (activeItems.length) {
			groups.push({
				key: 'active',
				heading: 'Currently open',
				testId: 'xoe-conversation-section-active',
				items: activeItems,
			});
		}

		return { mode: 'sectioned', groups, sectionedButEmpty: groups.length === 0 };
	}

	// ── 3. Date buckets ───────────────────────────────────────────────────────
	//
	// The list already arrives `last_message_at DESC`, so walking it in order and appending
	// to the bucket each row lands in inserts headings WITHOUT reordering anything. Empty
	// buckets never get created, so they cannot render.
	const order: string[] = [];
	const byBucket = new Map<string, RailGroup>();

	for (const conv of conversations) {
		const { slug, heading } = bucketFor(conv, now);
		let group = byBucket.get(slug);
		if (!group) {
			group = { key: slug, heading, groupSlug: slug, items: [] };
			byBucket.set(slug, group);
			order.push(slug);
		}
		group.items.push(conv);
	}

	return {
		mode: 'dated',
		groups: order.map((slug) => byBucket.get(slug)!),
		sectionedButEmpty: false,
	};
}
